import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  const refreshFavorites = useCallback(async () => {
    try {
      const { data } = await api.get("/favorites");
      setFavorites(data.ids || []);
    } catch {
      setFavorites([]);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    for (let attempt = 0; attempt < 4; attempt += 1) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        await refreshFavorites();
        break;
      } catch (e) {
        const status = e.response?.status;
        // only a real 401/403 means signed out; network/5xx blips are retried
        if ((status && status < 500) || attempt === 3) {
          setUser(null);
          setFavorites([]);
          break;
        }
        await new Promise((r) => setTimeout(r, 800 * 2 ** attempt));
      }
    }
    setLoading(false);
  }, [refreshFavorites]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      await refreshFavorites();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, [refreshFavorites]);

  const register = useCallback(async (name, email, password) => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      setUser(data);
      await refreshFavorites();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, [refreshFavorites]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed; clearing local session anyway", e);
    }
    setUser(null);
    setFavorites([]);
  }, []);

  const googleLogin = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const isFavorite = useCallback((id) => favorites.includes(id), [favorites]);

  const toggleFavorite = useCallback(async (id) => {
    if (!user) {
      setAuthModalOpen(true);
      return { needAuth: true };
    }
    const currently = favorites.includes(id);
    setFavorites((prev) => (currently ? prev.filter((x) => x !== id) : [...prev, id]));
    try {
      if (currently) await api.delete(`/favorites/${id}`);
      else await api.post(`/favorites/${id}`);
    } catch {
      // revert on failure
      setFavorites((prev) => (currently ? [...prev, id] : prev.filter((x) => x !== id)));
    }
    return { added: !currently };
  }, [user, favorites]);

  const value = {
    user,
    loading,
    favorites,
    isFavorite,
    toggleFavorite,
    refreshFavorites,
    login,
    register,
    logout,
    googleLogin,
    setUser,
    authModalOpen,
    openAuth: () => setAuthModalOpen(true),
    closeAuth: () => setAuthModalOpen(false),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
